import React, { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import pv from './Overlays.module.css'
import { apiJson } from '../../lib/api'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, useSlow } from '../../components/pending'
import { dayLabel, type BulkPreview, type Cycle, type LocationOption, type Option } from './model'
import { itemName } from './itemName'

// The bulk panel (V4 paBulkPanel): one field, a shared reason, and a preview
// of what will change, with each selected cycle it would leave alone and why.
// The server applies it as one audited batch (h 2521), one write per cycle it
// changes, and the batch is undone as one action.

export type BulkOp = 'include' | 'worker' | 'weight' | 'quality' | 'location' | 'batch' | 'receipt'

export const BULK_BUTTONS: { op: BulkOp; label: string }[] = [
  { op: 'include', label: 'Include' },
  { op: 'worker', label: 'Worker' },
  { op: 'weight', label: 'Net weight (KG)' },
  { op: 'quality', label: 'Quality' },
  { op: 'location', label: 'Location' },
  { op: 'batch', label: 'Batch' },
  { op: 'receipt', label: 'Receipt these' },
]

// V4 paQualities: every quality any set offers, once per name. The quality
// is sent by id, so where two sets give one name different credit, each is
// listed with its set's name instead of silently taking the first.
interface QualityChoice {
  id: string
  label: string
}
const NO_QUALITY = '__none__'

export function qualityChoices(sets: { name: string; qualities: { id: string; name: string; deducts: boolean; credited_pct: number }[] }[]): QualityChoice[] {
  const byName = new Map<string, { set: string; id: string; credit: number }[]>()
  for (const s of sets) {
    for (const q of s.qualities) {
      const credit = q.deducts ? q.credited_pct : 100
      const list = byName.get(q.name) || []
      if (!list.some((x) => x.credit === credit)) list.push({ set: s.name, id: q.id, credit })
      byName.set(q.name, list)
    }
  }
  const out: QualityChoice[] = []
  for (const [name, list] of byName) {
    for (const x of list) out.push({ id: x.id, label: list.length > 1 ? `${name} · ${x.set}` : name })
  }
  return out
}

const TITLE: Record<BulkOp, string> = {
  include: 'Include or exclude',
  worker: 'Credit these cycles to',
  weight: 'Set net weight',
  quality: 'Set quality',
  location: 'Set location',
  batch: 'Set batch',
  receipt: 'Receipt these',
}
const NOTE: Record<BulkOp, string> = {
  include: 'Excluded cycles keep their record and drop out of production totals.',
  worker: 'Every selected cycle is credited to this worker. The claims stay in each cycle’s timeline.',
  weight: 'Gross weight is replaced. Any quality factor is reapplied to the new figure.',
  quality: 'Applies one quality to every selected cycle and recalculates credited weight.',
  location: 'Use when a crew scanned in the wrong row. The location is stored as level numbers.',
  batch: 'Use when a batch was mis-keyed at the receipting station.',
  receipt: 'Marks Self Scans that no receipt has reached as receipted, now. Weight and pay do not change; undo it from the cycle’s adjustments.',
}

// The field and value a bulk change sends, or what is still missing. The
// preview and the apply send the same thing.
function requestOf(op: BulkOp, value: string): Record<string, unknown> | { error: string } {
  switch (op) {
    case 'batch':
      // Named by its ID; the server resolves it and refuses one at another
      // site or on another picking job (a batch holds one job, §7k.1).
      return value.trim() ? { field: 'batch_id', batch_code: value.trim() } : { error: 'Enter a batch ID' }
    case 'receipt':
      // A marker: nothing to choose, so no value is sent.
      return { field: 'receipted' }
    case 'include':
      return { field: 'status', value: value === 'Exclude' ? 'excluded' : 'included' }
    case 'worker':
      return value ? { field: 'human_id', value } : { error: 'Choose a worker' }
    case 'weight': {
      const n = parseFloat(value)
      return Number.isFinite(n) && n > 0 ? { field: 'gross_weight_kg', value: n } : { error: 'Enter a weight in kilograms' }
    }
    case 'location':
      return value ? { field: 'location', value } : { error: 'Choose a location' }
    case 'quality':
      // A quality from a set, or "No quality"; never a typed factor.
      return value ? { field: 'qc_factor', quality_id: value === NO_QUALITY ? null : value } : { error: 'Pick a quality' }
  }
}

interface Props {
  user: { getIdToken: () => Promise<string> } | null
  op: BulkOp
  cycles: Cycle[]
  lockedDate: (date: string) => boolean
  workers: Option[]
  locations: LocationOption[]
  onClose: () => void
  // The batch the change made (null when it changed nothing), so the page
  // can offer undoing it as one action.
  onApplied: (message: string, bulkEditId: string | null) => void
  onError: (message: string) => void
}

export function BulkModal({ user, op, cycles, lockedDate, workers, locations, onClose, onApplied, onError }: Props) {
  const [value, setValue] = useState(op === 'include' ? 'Include' : '')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [qualities, setQualities] = useState<QualityChoice[] | null>(null)
  const slow = useSlow(busy)
  // h 425-458: Escape, the scrim and Cancel ask first once a value or reason is entered.
  // Nothing closes while the write is out (busy).
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ value, reason })
  const { requestClose, confirm } = useLayer(true, onClose, { busy, dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true })

  useEffect(() => {
    if (op !== 'quality') return
    let live = true
    apiJson<{ sets: Parameters<typeof qualityChoices>[0] }>(user, '/api/quality')
      .then((r) => live && setQualities(qualityChoices(r.sets)))
      .catch((e) => live && onError(e?.message || 'Could not load the quality sets'))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op, user])

  const rows = cycles.filter((c) => !lockedDate(c.work_date))
  const blocked = cycles.length - rows.length

  // A location belongs to one site, so only the sites of the selection are offered.
  const sites = new Set(cycles.map((c) => c.site_id))
  const locOptions = locations.filter((l) => sites.has(l.site_id)).sort((a, b) => a.label.localeCompare(b.label))

  // h 2521: "Blocked rows listed with reasons before committing". Once the
  // value is complete the server plans the change (the same rules the apply
  // runs, writing nothing) and lists each selected cycle it would leave alone.
  // Until then, the count is the locked-day rule worked out here.
  const req = requestOf(op, value)
  const reqKey = 'error' in req ? '' : JSON.stringify(req)
  const ids = cycles.map((c) => c.id)
  const idsKey = ids.join(',')
  const [preview, setPreview] = useState<BulkPreview | null>(null)
  const [previewErr, setPreviewErr] = useState('')
  useEffect(() => {
    setPreview(null)
    setPreviewErr('')
    if (!reqKey) return
    let live = true
    const t = setTimeout(() => {
      apiJson<BulkPreview>(user, '/api/product-activity/bulk/preview', { body: { cycle_ids: ids, ...JSON.parse(reqKey) } })
        .then((r) => live && setPreview(r))
        .catch((e) => live && setPreviewErr(e?.message || 'Could not check the selection'))
    }, 250)
    return () => {
      live = false
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqKey, idsKey, user])
  const nothing = !!preview && preview.changing.length === 0

  const apply = async () => {
    if (busy) return
    if ('error' in req) return onError(String(req.error))
    setBusy(true)
    try {
      const r = await apiJson<{ message: string; bulk_edit_id?: string | null }>(user, '/api/product-activity/bulk', {
        body: { cycle_ids: ids, ...req, reason: reason.trim() },
      })
      onApplied(r.message, r.bulk_edit_id || null)
    } catch (e: any) {
      onError(e?.message || 'The bulk change was not saved')
    } finally {
      setBusy(false)
    }
  }

  const label = op === 'include' ? 'Set to' : op === 'worker' ? 'Worker' : op === 'weight' ? 'Net weight (KG)' : op === 'batch' ? 'Batch ID' : op === 'quality' ? 'Quality' : 'Location'

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${pv.bulkModal}`} role="dialog" aria-label={TITLE[op]}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{TITLE[op]}</h2>
            <p className={o.sub}>{NOTE[op]}</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          {op !== 'receipt' && (
          <label className={`${o.field} ${pv.fieldShort}`}>
            <span className={o.fieldLabel}>{label}</span>
            {op === 'weight' ? (
              <input className={o.input} value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 5.2" inputMode="decimal" />
            ) : op === 'batch' ? (
              <input className={o.input} value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. B-1042" />
            ) : (
              <select className={o.input} value={value} onChange={(e) => setValue(e.target.value)}>
                {op === 'include' && (
                  <>
                    <option value="Include">Include</option>
                    <option value="Exclude">Exclude</option>
                  </>
                )}
                {op === 'worker' && (
                  <>
                    <option value="">Pick a worker</option>
                    {workers.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </>
                )}
                {op === 'quality' && (
                  <>
                    <option value="">{qualities ? 'Pick a quality' : 'Loading qualities…'}</option>
                    {(qualities || []).map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.label}
                      </option>
                    ))}
                    <option value={NO_QUALITY}>No quality</option>
                  </>
                )}
                {op === 'location' && (
                  <>
                    <option value="">Pick a location</option>
                    {locOptions.map((l) => (
                      <option key={l.id} value={l.id}>
                        {sites.size > 1 ? l.site_name + ' · ' + l.label : l.label}
                      </option>
                    ))}
                  </>
                )}
              </select>
            )}
          </label>
          )}
          <label className={o.field}>
            <span className={o.fieldLabel}>Reason: shared by every audit entry this creates. Optional.</span>
            <input className={o.input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Crew scanned the wrong row" />
          </label>
          <div className={pv.countBlock} data-testid="bulk-count">
            {preview ? (
              <>
                <span className={pv.countLine}>
                  {preview.changing.length} of {preview.selected} selected cycles will change
                </span>
                {!nothing && <span className={pv.countWhat}>{preview.what}</span>}
                {preview.blocked.length > 0 && (
                  <>
                    <span className={pv.blockedLine}>
                      {preview.blocked.length} will not change{nothing ? '. Nothing would be written' : ''}:
                    </span>
                    <ul className={pv.blockedList} aria-label="Cycles that will not change">
                      {preview.blocked.map((b) => (
                        <li key={b.id}>
                          <b>{itemName(b) || 'Cycle'}</b>
                          {b.work_date && <span>{dayLabel(b.work_date)}</span>}
                          <em>{b.text}</em>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            ) : (
              <>
                <span className={pv.countLine}>
                  {rows.length} of {cycles.length} selected cycles will change
                </span>
                {blocked > 0 && <span className={pv.blockedLine}>{blocked} on locked days cannot be changed</span>}
              </>
            )}
            {previewErr && <span className={pv.blockedLine}>{previewErr}</span>}
          </div>
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <PendingButton busy={busy} pending="Applying…" className={o.primary} onClick={apply} disabled={nothing}>
            Apply and audit
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}
